import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { useSelector } from "react-redux";

import Grid from "@material-ui/core/Grid";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";

import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import Paper from "@material-ui/core/Paper";
import TableFooter from "@material-ui/core/TableFooter";
import TextField from "@material-ui/core/TextField";

import axios from "axios";
import envConfig from "../../bin/env.config";
import CompanyLogo from "../Common/CompanyLogo";
import StoreCitySelector from "../City/StoreCitySelector";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(4),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
    },
  },
  table: {
    minWidth: 350,
    "& tbody>.MuiTableRow-root:hover": {
      background: "#DDD",
    },
  },
  title: {
    flexGrow: 1,
    display: "flex",
    alignItems: "center",
    [theme.breakpoints.up("sm")]: {
      display: "flex",
    },
  },
  button: {
    marginTop: "2px",
    marginRight: "8px",
  },
  textbox: {
    marginTop: "2px",
    marginRight: "8px",
    fontFamily: "Montserrat",
    textTransform: "none",
    fontSize: "1rem",
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: "0.00938em",
    width: "50px",
  },
  buttonText: {
    fontFamily: "Montserrat",
    textTransform: "none",
  },
  searchField: {
    marginRight: theme.spacing(2),
    width: 300,
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
}));

const STATUS_OPTIONS = {
  0: "All",
  1: "Mapped with city",
  2: "Not mapped",
};

export default function CityStoresPage(props) {
  const history = useHistory();
  const classes = useStyles();
  const token = useSelector((state) => state.session.token);

  const [isLoading, setIsLoading] = useState(false);
  const [stores, setStores] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(0);
  const [statusAnchorEl, setStatusAnchorEl] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("store_id");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(50);
  const [skip, setSkip] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const loader = document.querySelector(".loader");

  const showLoader = () => loader && loader.classList.remove("loader--hide");
  const hideLoader = () => loader && loader.classList.add("loader--hide");

  // Calculate paginated data from server response
  const totalPages = Math.ceil(totalCount / limit);

  useEffect(() => {
    const fetchData = async () => {
      showLoader();
      setIsLoading(true);
      try {
        const url = `${envConfig.API_ROOT}${envConfig.API_VERSION}/city/stores?orderBy=${orderBy}&order=${order}&limit=${limit}&skip=${skip}`;
        const result = await axios.post(
          url,
          {
            q: searchText,
            status: selectedStatus,
          },
          {
            headers: {
              Authorization: JSON.stringify(token),
            },
          }
        );

        if (result.data.success && result.data.data) {
          setStores(result.data.data);
          // If API returns total count, use it; otherwise calculate from data length
          if (result.data.total !== undefined) {
            setTotalCount(result.data.total);
          } else {
            // Fallback: if we got less than limit, that's the total
            setTotalCount(result.data.data.length < limit ? skip + result.data.data.length : skip + result.data.data.length + 1);
          }
        }
      } catch (error) {
        console.error("Error fetching stores:", error);
        setStores([]);
        setTotalCount(0);
      } finally {
        setIsLoading(false);
        hideLoader();
      }
    };

    fetchData();
  }, [token, selectedStatus, searchText, order, orderBy, skip, limit]);

  const handleStatusClick = (event) => {
    setStatusAnchorEl(event.currentTarget);
  };

  const handleStatusClose = () => {
    setStatusAnchorEl(null);
  };


  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
    setSkip(0);
    setCurrentPage(1);
  };

  const handleSort = (column) => {
    const isAsc = orderBy === column && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(column);
    setSkip(0);
    setCurrentPage(1);
  };

  const handleStatusSelect = (status) => {
    setSelectedStatus(parseInt(status));
    setSkip(0);
    setCurrentPage(1);
    handleStatusClose();
  };

  const handleAutoMapping = async () => {
    showLoader();
    setIsLoading(true);
    try {
      const url = `${envConfig.API_ROOT}${envConfig.API_VERSION}/city/auto_mapping`;
      const result = await axios.post(
        url,
        {},
        {
          headers: {
            Authorization: JSON.stringify(token),
          },
        }
      );

      if (result.data.success) {
        const count = result.data.data?.count || result.data.count || 0;
        alert(`Successfully auto-mapped ${count} record(s).`);
        window.location.reload();
      } else {
        alert("An error occurred while performing auto mapping.");
      }
    } catch (error) {
      console.error("Error in auto mapping:", error);
      alert("An error occurred while performing auto mapping. Please try again.");
    } finally {
      setIsLoading(false);
      hideLoader();
    }
  };

  const go2Page = (page) => {
    const pageNum = parseInt(page);
    if (pageNum >= 1 && pageNum <= totalPages) {
      setCurrentPage(pageNum);
      setSkip((pageNum - 1) * limit);
    }
  };

  return (
    <>
      <AppBar position="sticky" color="inherit">
        <Toolbar>
          <IconButton
            color="inherit"
            onClick={() => history.goBack()}
            aria-label="Close"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography className={classes.title} variant="h6" noWrap>
            City Stores
          </Typography>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search by name, address, city, province"
            value={searchText}
            onChange={handleSearchChange}
            className={classes.searchField}
          />
          <Button
            variant="outlined"
            onClick={handleStatusClick}
            style={{ minWidth: 200, justifyContent: "flex-start", marginRight: "16px" }}
            endIcon={<ExpandMoreIcon />}
          >
            {STATUS_OPTIONS[selectedStatus]}
          </Button>
          <Menu
            anchorEl={statusAnchorEl}
            keepMounted
            open={Boolean(statusAnchorEl)}
            onClose={handleStatusClose}
          >
            {Object.keys(STATUS_OPTIONS).map((key) => (
              <MenuItem
                key={key}
                onClick={() => handleStatusSelect(key)}
                selected={selectedStatus === parseInt(key)}
              >
                <ListItemText>{STATUS_OPTIONS[key]}</ListItemText>
              </MenuItem>
            ))}
          </Menu>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAutoMapping}
            disabled={isLoading}
            style={{ marginLeft: "8px" }}
          >
            Auto Mapping
          </Button>
        </Toolbar>
      </AppBar>
      {isLoading ? (
        <Grid
          className={classes.root}
          container
          alignItems="center"
          justifyContent="center"
        >
          <div>Loading ...</div>
        </Grid>
      ) : (
        <Grid
          className={classes.root}
          container
          alignItems="center"
          justifyContent="center"
        >
          <TableContainer component={Paper}>
            <Table className={classes.table} aria-label="city stores table">
              <TableHead>
                <TableRow>
                  <TableCell align="left">
                    <TableSortLabel
                      active={orderBy === "store_id"}
                      direction={orderBy === "store_id" ? order : "asc"}
                      onClick={() => handleSort("store_id")}
                    >
                      Store ID
                      {orderBy === "store_id" ? (
                        <span className={classes.visuallyHidden}>
                          {order === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </span>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="left">
                    <TableSortLabel
                      active={orderBy === "company_id"}
                      direction={orderBy === "company_id" ? order : "asc"}
                      onClick={() => handleSort("company_id")}
                    >
                      Company
                      {orderBy === "company_id" ? (
                        <span className={classes.visuallyHidden}>
                          {order === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </span>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="left">
                    <TableSortLabel
                      active={orderBy === "name"}
                      direction={orderBy === "name" ? order : "asc"}
                      onClick={() => handleSort("name")}
                    >
                      Name
                      {orderBy === "name" ? (
                        <span className={classes.visuallyHidden}>
                          {order === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </span>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>                  
                  <TableCell align="left">
                    <TableSortLabel
                      active={orderBy === "city"}
                      direction={orderBy === "city" ? order : "asc"}
                      onClick={() => handleSort("city")}
                    >
                      Parsed City
                      {orderBy === "city" ? (
                        <span className={classes.visuallyHidden}>
                          {order === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </span>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                  
                  
                 
                  <TableCell align="left">
                    <TableSortLabel
                      active={orderBy === "city_id"}
                      direction={orderBy === "city_id" ? order : "asc"}
                      onClick={() => handleSort("city_id")}
                    >
                      Corrected City
                      {orderBy === "city_id" ? (
                        <span className={classes.visuallyHidden}>
                          {order === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </span>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stores.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No stores found
                    </TableCell>
                  </TableRow>
                ) : (
                  stores.map((store, index) => (
                    <TableRow key={store.store_id || index}>
                      <TableCell align="left">{store.store_id}</TableCell>
                      <TableCell align="left">
                        <CompanyLogo company_id={store.company_id} alt={store.company_id} style={{ height: "30px" }} />
                      </TableCell>
                      <TableCell align="left">{store.name}<br/><i>{store.address}</i></TableCell>
                      <TableCell align="left">{store.city} <br/><i>{store.province}</i> - {store.postal}</TableCell>
                      
                      <TableCell align="left" style={{ backgroundColor: store.city_id?"rgba(0, 255, 0, 0.1) ":"rgba(255, 0, 0, 0.1)"}}>
                        <StoreCitySelector store={store} />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    {totalPages > 0 && (
                      <>
                        <button
                          className={classes.button}
                          onClick={() => go2Page(currentPage - 1)}
                          disabled={currentPage === 1}
                          variant="outlined"
                        >
                          <Typography className={classes.buttonText} noWrap>
                            Go Back
                          </Typography>
                        </button>
                        <input
                          className={classes.textbox}
                          type="number"
                          min="1"
                          max={totalPages}
                          value={currentPage}
                          onChange={(e) => go2Page(e.target.value)}
                        />
                        <Typography
                          component="span"
                          style={{ marginRight: "8px" }}
                        >
                          / {totalPages}
                        </Typography>
                        <button
                          className={classes.button}
                          onClick={() => go2Page(currentPage + 1)}
                          disabled={currentPage >= totalPages}
                          variant="outlined"
                        >
                          <Typography className={classes.buttonText} noWrap>
                            Go Next
                          </Typography>
                        </button>
                        <Typography
                          component="span"
                          style={{ marginLeft: "16px" }}
                        >
                          Showing {skip + 1}-
                          {Math.min(skip + limit, totalCount)} of {totalCount}{" "}
                          stores
                        </Typography>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </Grid>
      )}
    </>
  );
}

